'use client';

import { List, Text } from '@mantine/core';
import React from 'react';

interface SafeMarkdownProps {
  content: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  color?: string;
  /** Optional icon for unordered-list bullets (e.g. the brand star). */
  listIcon?: React.ReactNode;
}

export function SafeMarkdown({
  content,
  size = 'sm',
  color,
  listIcon,
}: Readonly<SafeMarkdownProps>) {
  // First, handle escaped newlines if they exist (from JSON storage)
  const processedContent = content.replace(/\\n/g, '\n');

  // Split content by double newlines to create paragraphs
  const paragraphs = processedContent.split(/\n\n+/);

  const renderParagraph = (paragraph: string, index: number) => {
    // Check if this is a list (starts with - or * or digit.)
    const listItems = paragraph.split('\n').filter(line => line.trim());
    const isUnorderedList = listItems.every(line =>
      /^[*-]\s+/.test(line.trim()),
    );
    const isOrderedList = listItems.every(line =>
      /^\d+\.\s+/.test(line.trim()),
    );

    if (isUnorderedList || isOrderedList) {
      const items = listItems.map(line =>
        line
          .replace(/^[*-]\s+/, '')
          .replace(/^\d+\.\s+/, '')
          .trim(),
      );

      return (
        <List
          key={index}
          size={size}
          type={isOrderedList ? 'ordered' : 'unordered'}
          icon={isUnorderedList ? listIcon : undefined}
          withPadding
        >
          {items.map((item, i) => (
            <List.Item key={i}>
              <Text size={size} c={color}>
                {item}
              </Text>
            </List.Item>
          ))}
        </List>
      );
    }

    // Handle line breaks within paragraphs
    const lines = paragraph.split('\n');

    return (
      <Text key={index} size={size} c={color}>
        {lines.map((line, i) => (
          <React.Fragment key={i}>
            {line}
            {i < lines.length - 1 && <br />}
          </React.Fragment>
        ))}
      </Text>
    );
  };

  return (
    <>
      {paragraphs.map((paragraph, index) => (
        <React.Fragment key={index}>
          {renderParagraph(paragraph, index)}
        </React.Fragment>
      ))}
    </>
  );
}
